import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UIState {
  // Sidebar state
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  
  // Theme
  theme: 'light' | 'dark' | 'system';
  
  // Notifications
  unreadNotificationsCount: number;
  
  // Modals/Drawers
  activeModal: string | null;
  modalData: unknown;
  
  // Actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (open: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setUnreadNotificationsCount: (count: number) => void;
  openModal: (modalId: string, data?: unknown) => void;
  closeModal: () => void;
  incrementUnreadCount: () => void;
  decrementUnreadCount: () => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      theme: 'system',
      unreadNotificationsCount: 0,
      activeModal: null,
      modalData: null,

      // Actions
      toggleSidebar: () => {
        const newState = !get().sidebarCollapsed;
        set({ sidebarCollapsed: newState });
      },

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      toggleMobileSidebar: () => {
        set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen }));
      },

      setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),

      setTheme: (theme) => {
        set({ theme });
        
        // Apply theme to document
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        
        if (theme === 'system') {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light';
          root.classList.add(systemTheme);
        } else {
          root.classList.add(theme);
        }
      },

      setUnreadNotificationsCount: (count) => set({ unreadNotificationsCount: count }),

      incrementUnreadCount: () => {
        set((state) => ({ 
          unreadNotificationsCount: state.unreadNotificationsCount + 1 
        }));
      },

      decrementUnreadCount: () => {
        set((state) => ({ 
          unreadNotificationsCount: Math.max(0, state.unreadNotificationsCount - 1)
        }));
      },

      openModal: (modalId, data = null) => set({ 
        activeModal: modalId, 
        modalData: data 
      }),

      closeModal: () => set({ activeModal: null, modalData: null }),
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({ 
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme 
      }),
    }
  )
);
